<template>
  <v-container class="main-container d-flex flex-column justify-center">
    <!-- Loading-->
    <v-row v-if="(!schemes && !cannotLoad) || trainerSettingsLoading">
      <v-col cols="12" class="mt-5">
        <div class="text-center mb-5">
          <v-progress-circular
            :size="50"
            :width="7"
            :color="uiColor"
            indeterminate
            :aria-label="$t('loading')"
            aria-busy="true"
            aria-live="polite"
          ></v-progress-circular>
        </div>
      </v-col>
    </v-row>
    <v-row v-if="cannotLoad">
      <v-col cols="12" md="10" offset="0" offset-md="1" class="pt-4 textfield-margin pb-5">
        <v-alert dense color="#f0404f" outlined>
          <v-icon left color="#f0404f">$vuetify.icons.closeCircle</v-icon
          ><span>{{ $t("failed_to_load") }}</span></v-alert
        >
      </v-col>
    </v-row>
    <v-row v-if="noIdCMP">
      <v-col cols="12" md="10" offset="0" offset-md="1" class="pt-4 textfield-margin pb-5">
        <v-alert dense color="#f0404f" outlined>
          <v-icon left color="#f0404f">$vuetify.icons.closeCircle</v-icon><span>{{ $t("no_id_cmp") }}</span></v-alert
        >
      </v-col>
    </v-row>
    <div v-if="schemes && !cannotLoad && !noIdCMP && !trainerSettingsLoading">
      <!-- CERTIFICATE EXPIRED -->
      <v-row v-if="certificateExpired">
        <v-col cols="12" md="10" offset="0" offset-md="1" class="pt-4 textfield-margin pb-5">
          <v-alert dense color="#f0404f" outlined>
            <v-icon left color="#f0404f">$vuetify.icons.closeCircle</v-icon
            ><span>{{ $t("certificate_expired") }}</span></v-alert
          >
        </v-col>
      </v-row>

      <!-- CERTIFICATE -->
      <v-row no-gutters align="center">
        <v-col
          cols="8"
          md="3"
          offset="2"
          offset-md="1"
          class="align--right"
          :class="{ 'text-margin': $t('certificate_chooser.1.1') }"
        >
          <span class="pl-3 pl-md-0 pr-3 text-label" v-if="$t('certificate_chooser.1.1')">{{
            $t("certificate_chooser.1.1")
          }}</span>
        </v-col>
        <v-col cols="8" md="5" offset="2" offset-md="0" class="dropdown">
          <ExamSelectorDropdown
            :owners="schemes"
            :inputError="$v.selectedCertificate.$error"
            class="dropdown-content"
            :label="$t('certificate_chooser.1.2')"
            :disabled="paperExam"
          />
          <div v-if="$v.selectedCertificate.$error" class="input-error--info-text">
            {{ $i18n.t("required_field") }}
          </div>
        </v-col>
        <v-col cols="8" md="3" offset="2" offset-md="0" :class="{ 'text-margin': $t('certificate_chooser.1.3') }">
          <span class="pr-3 pr-md-0 pl-3 text-label" v-if="$t('certificate_chooser.1.3')">{{
            $t("certificate_chooser.1.3")
          }}</span>
        </v-col>
      </v-row>

      <!-- COUNTRY -->
      <v-row no-gutters align="center" v-if="tenant != 'as' && !hideCountry">
        <v-col
          cols="8"
          md="3"
          offset="2"
          offset-md="1"
          class="align--right"
          :class="{ 'text-margin': $t('certificate_chooser.2.1') }"
        >
          <span class="pl-3 pl-md-0 pr-3 text-lable" v-if="$t('certificate_chooser.2.1')">{{
            $t("certificate_chooser.2.1")
          }}</span>
        </v-col>
        <v-col cols="8" md="5" offset="2" offset-md="0" class="dropdown">
          <v-autocomplete
            :items="getAvalilableCountries"
            item-text="name"
            item-value="code"
            class="select"
            attach
            solo
            dense
            :label="$t('certificate_chooser.2.2')"
            :color="uiColor"
            :disabled="!selectedCertificate || paperExam"
            v-model="selectedCountry"
            item-:color="uiColor"
            :class="{
              'input-error': $v.selectedCountry.$error,
              focused: focused2,
            }"
            @focus="focused2 = true"
            @blur="focused2 = false"
            data-test="select-country"
            :aria-label="$t('certificate_chooser.2.2')"
            :aria-disabled="!selectedCertificate || paperExam"
          ></v-autocomplete>
          <div v-if="$v.selectedCountry.$error" class="input-error--info-text">
            {{ $i18n.t("required_field") }}
          </div>
        </v-col>
        <v-col cols="8" md="3" offset="2" offset-md="0" class="{ 'text-margin':$t('certificate_chooser.2.3') }">
          <span class="pr-3 pr-md-0 pl-3 text-label" v-if="$t('certificate_chooser.2.3')">{{
            $t("certificate_chooser.2.3")
          }}</span>
        </v-col>
      </v-row>

      <!-- LANGUAGE -->
      <v-row no-gutters align="center">
        <v-col
          cols="8"
          md="3"
          offset="2"
          offset-md="1"
          class="align--right"
          :class="{ 'text-margin': $t('certificate_chooser.3.1') }"
        >
          <span class="pl-3 pl-md-0 pr-3 text-label" v-if="$t('certificate_chooser.3.1')">{{
            $t("certificate_chooser.3.1")
          }}</span>
        </v-col>
        <v-col cols="8" md="5" offset="2" offset-md="0" class="dropdown">
          <v-autocomplete
            :items="availableLanguages"
            item-text="name"
            item-value="code"
            attach
            solo
            dense
            :label="$t('certificate_chooser.3.2')"
            class="select"
            :color="uiColor"
            :disabled="!selectedCountry || !selectedCertificate || paperExam"
            v-model="selectedLanguage"
            item-:color="uiColor"
            :class="{
              'input-error': $v.selectedLanguage.$error,
              focused: focused3,
            }"
            @focus="focused3 = true"
            @blur="focused3 = false"
            data-test="select-language"
            :aria-label="$t('certificate_chooser.3.2')"
            :aria-disabled="!selectedCountry || !selectedCertificate || paperExam"
          ></v-autocomplete>
          <div v-if="$v.selectedLanguage.$error" class="input-error--info-text">
            {{ $i18n.t("required_field") }}
          </div>
        </v-col>
        <v-col cols="8" md="3" offset="2" offset-md="0" :class="{ 'text-margin': $t('certificate_chooser.3.3') }">
          <span class="pr-3 pr-md-0 pl-3 text-label" v-if="$t('certificate_chooser.3.3')">{{
            $t("certificate_chooser.3.3")
          }}</span>
        </v-col>
      </v-row>
      <!-- MODE -->
      <v-row
        no-gutters
        align="center"
        v-if="
          !(
            selectedCertificate &&
            selectedCertificate.overrides &&
            selectedCertificate.overrides.options &&
            selectedCertificate.overrides.options.showExamVariant === false
          ) && !paperExam
        "
      >
        <v-col
          cols="8"
          md="3"
          offset="2"
          offset-md="1"
          class="align--right"
          :class="{ 'text-margin': $t('certificate_chooser.4.1') }"
        >
          <span class="pl-3 pl-md-0 pr-3 text-label" v-if="$t('certificate_chooser.4.1')">{{
            $t("certificate_chooser.4.1")
          }}</span>
        </v-col>
        <v-col cols="8" md="5" offset="2" offset-md="0" class="dropdown">
          <v-select
            :items="liveDatesAvailable.length > 0 ? [$t('online'), $t('live')] : [$t('online')]"
            attach
            solo
            dense
            :label="$t('certificate_chooser.4.2')"
            class="select"
            :color="uiColor"
            :disabled="!selectedLanguage || examPreloaded"
            v-model="selectedMode"
            item-:color="uiColor"
            :class="{
              'input-error': $v.selectedMode.$error,
              focused: focused4,
            }"
            @focus="focused4 = true"
            @blur="focused4 = false"
            data-test="select-exam-mode"
            :aria-label="$t('certificate_chooser.4.2')"
            :aria-disabled="!selectedLanguage || examPreloaded"
          >
            <template v-slot:selection="data">
              <span :aria-disabled="!selectedLanguage || examPreloaded" v-if="data.item">{{ data.item }}</span>
            </template>
          </v-select>
          <div v-if="$v.selectedMode.$error" class="input-error--info-text">
            {{ $i18n.t("required_field") }}
          </div>
        </v-col>
        <v-col
          cols="8"
          md="3"
          offset="2"
          offset-md="0"
          align="left"
          :class="{ 'text-margin': $t('certificate_chooser.4.3') }"
        >
          <span class="pr-3 pr-md-0 pl-3 text-label" v-if="$t('certificate_chooser.4.3')">{{
            $t("certificate_chooser.4.3")
          }}</span>
        </v-col>
      </v-row>
      <!--<v-row no-gutters align="center" v-if="selectedCertificate && selectedCountry" class="mb-5">
        <v-col cols="10" offset="1" class="align--center">
          <span class="pl-3 pl-md-0 pr-0 text-label"
            >{{ $t("available_mode") }} {{ $t("online") }}</span
          >
          <span class="pl-0 pl-md-0 pr-3 text-label" v-if="liveDatesAvailable.length > 0"
            >, {{ $t("live") }}</span
          >
        </v-col>
      </v-row>-->
    </div>
    <!-- Navigation Buttons -->
    <div>
      <v-btn
        v-if="!noIdCMP && !cannotLoad && !trainerSettingsLoading"
        tile
        :color="uiColor"
        class="next-button"
        v-on:click="validateForm()"
        :disabled="certificateExpired"
        :aria-disabled="certificateExpired"
        data-test="button-next"
        >{{ $t("proceed") }}</v-btn
      >
    </div>
  </v-container>
</template>

<script>
import ExamSelectorDropdown from "./ExamSelectorDropdown";
import { required, requiredIf, helpers } from "vuelidate/lib/validators";

const contains = (param) =>
  helpers.withParams(
    { type: "contains", value: param },
    (value) => helpers.req(value) && helpers.req(param) && param.map((lang) => lang.code).includes(value)
  );

export default {
  name: "ExamInfo",
  components: {
    ExamSelectorDropdown,
  },
  data() {
    return {
      focused2: false,
      focused3: false,
      focused4: false,
      uiColor: this.$store.getters["registration/uiColor"],
      tenant: this.$store.getters["registration/tenant"],
    };
  },
  validations() {
    return {
      selectedCertificate: {
        required,
      },
      selectedCountry: {
        required,
      },
      selectedMode: {
        required: requiredIf(function () {
          return !(
            this.selectedCertificate &&
            this.selectedCertificate.overrides &&
            this.selectedCertificate.overrides.options &&
            this.selectedCertificate.overrides.options.showExamVariant === false
          );
        }),
      },
      selectedLanguage: {
        required,
        contains: contains(this.$store.getters["registration/availableLanguagesForSyllabus"]),
      },
    };
  },
  methods: {
    validateForm() {
      this.$v.$touch();
      if (!this.$v.$error) {
        this.$emit("next-button-clicked");
      }
    },
  },
  computed: {
    trainerSettingsLoading() {
      return this.$store.getters["registration/trainerSettingsLoading"];
    },
    hideCountry() {
      return this.$store.getters["registration/hideCountry"];
    },
    noIdCMP() {
      return this.$store.getters["registration/noIdCMP"];
    },
    cannotLoad() {
      return this.$store.getters["registration/cannotLoad"];
    },
    schemes() {
      return this.$store.getters["registration/schemes"];
    },
    paperExam() {
      return this.$store.getters["registration/paperExam"];
    },
    examPreloaded() {
      return this.$store.getters["registration/examPreloaded"];
    },
    certPreloaded() {
      return this.$store.getters["registration/certPreloaded"];
    },
    certificateExpired() {
      return this.$store.getters["registration/certificateExpired"];
    },
    languagePreloaded() {
      return this.$store.getters["registration/languagePreloaded"];
    },
    getAvalilableCountries() {
      return this.$store.getters["registration/availableCountriesForCertificate"];
    },
    availableLanguages() {
      return this.$store.getters["registration/availableLanguagesForSyllabus"];
    },
    selectedCertificate: {
      get() {
        return this.$store.getters["registration/certificate"];
      },
      set(value) {
        this.$store.dispatch("registration/updateCertificate", value);
      },
    },
    selectedCountry: {
      get() {
        return this.$store.getters["registration/country"];
      },
      set(value) {
        this.$store.dispatch("registration/updateCountry", value);
      },
    },
    selectedMode: {
      get() {
        return this.$store.getters["registration/mode"];
      },
      set(value) {
        this.$store.dispatch("registration/updateMode", value);
        this.$store.dispatch("registration/resetDefaults");
      },
    },
    selectedLanguage: {
      get() {
        return this.$store.getters["registration/language"];
      },
      set(value) {
        this.$store.dispatch("registration/updateLanguage", value);
        if (!this.$store.getters["registration/mode"]) {
          this.$store.dispatch("registration/updateMode", this.$t("online"));
        }
      },
    },
    liveDatesAvailable() {
      return this.$store.getters["registration/liveDatesAvailable"];
    },
  },
};
</script>

<style scoped>
.main-container {
  height: 100%;
}
.v-text-field.v-text-field--enclosed .v-text-field__details {
  height: 0;
  padding: 0;
  margin: 0;
}
.text-margin {
  margin-bottom: 24px;
}
.text-label {
  font-size: 18px;
}
.dropdown-content {
  display: block;
}

@media (max-width: 960px) {
  .align--right {
    text-align: left !important;
  }
  .main-container {
    height: fit-content !important;
  }
  .text-margin {
    margin-bottom: 12px;
  }
  .dropdown {
    margin-bottom: -12px;
  }
}

.align--right {
  text-align: right;
}

.align--center {
  text-align: center !important;
}

.input-error ::v-deep .v-input__slot {
  box-shadow: 0px 3px 1px -2px rgba(240, 64, 79, 0.2), 0px 2px 2px 0px rgba(240, 64, 79, 0.2),
    0px 1px 5px 0px rgba(240, 64, 79, 0.2) !important;
}

.select.focused ::v-deep .v-input__slot {
  box-shadow: 0px 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 6px 10px 0px rgba(0, 0, 0, 0.14),
    0px 1px 18px 0px rgba(0, 0, 0, 0.12) !important;
}

.select-cursor ::v-deep input {
  cursor: pointer !important;
}

.noselect ::v-deep input {
  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Old versions of Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none;
}

.select-cursor ::v-deep .v-input__slot {
  cursor: pointer !important;
}
</style>
